/**
 * Shared "step" schema — the bridge between Workbench notebooks and Logic Studio.
 * ===========================================================================
 * A notebook STEP is a Logic Studio *block* embedded in a notebook cell. The two
 * surfaces deliberately share one schema so a notebook can be serialized into a
 * Logic Function with no translation (1:1), and into a Pipeline / Dashboard with
 * a thin best-effort mapping.
 *
 * The canonical block contract lives in two mirrored places that MUST stay in
 * sync:
 *   - this file (frontend / generator output / serializers)
 *   - backend/shared/notebook_steps.py (generation validation + step preview)
 * and is the same shape consumed by backend/logic_service/runner.py.
 */

import { NODE_TYPE_DEFS } from '../modules/pipeline/pipelineTypes';
import { asCodeProposal } from '../modules/pipeline/codeConfig';

const VALID_PIPELINE_NODE_TYPES = new Set(NODE_TYPE_DEFS.map((d) => d.type as string));

/** Block types that the generator + UI surface as first-class notebook steps. */
export const NOTEBOOK_STEP_TYPES = [
  'ontology_query',
  'transform',
  'llm_call',
  'code',
  'ontology_update',
  'action',
  'file_upload',
  'pipeline_node',
] as const;

export type StepType = (typeof NOTEBOOK_STEP_TYPES)[number];

/**
 * A notebook step == a Logic Studio block. Fields beyond `id`/`type` are
 * type-specific and passed through verbatim to the logic runner. We keep this
 * permissive (index signature) rather than a tagged union because the runner
 * itself dispatches dynamically on `type` and reads heterogeneous fields
 * (config for ontology_query/code, prompt_template for llm_call, etc.).
 */
export interface NotebookStep {
  id: string;
  type: string;
  label?: string;
  config?: Record<string, unknown>;
  // llm_call
  prompt_template?: string;
  system_prompt?: string;
  model?: string;
  output_schema?: Record<string, unknown>;
  max_tokens?: number;
  // action
  action_name?: string;
  params?: Record<string, unknown>;
  reasoning?: string;
  // pipeline_node — a passthrough wrapper around one backend PipelineNode
  // (see types/pipeline.ts NodeType). `config` above carries its exact
  // NODE_TYPE_DEFS field names verbatim.
  node_type?: string;
  [key: string]: unknown;
}

export const STEP_TYPE_META: Record<
  string,
  { label: string; color: string; desc: string }
> = {
  ontology_query: { label: 'Ontology Query', color: '#3B82F6', desc: 'Fetch records from the ontology' },
  transform: { label: 'Transform', color: '#10B981', desc: 'Reshape data in memory' },
  llm_call: { label: 'LLM Call', color: '#7C3AED', desc: 'Call Claude with a prompt' },
  code: { label: 'Code', color: '#334155', desc: 'Python with the nexus SDK (sandboxed)' },
  ontology_update: { label: 'Ontology Update', color: '#059669', desc: 'Write fields back to records' },
  action: { label: 'Action', color: '#F59E0B', desc: 'Run a registered action' },
  file_upload: { label: 'Upload File', color: '#0E7490', desc: 'Write a file into SharePoint/OneDrive or Dropbox via a connector' },
  pipeline_node: { label: 'Pipeline Node', color: '#1A3C6E', desc: 'One pipeline node, any type — exports 1:1 into a Pipeline' },
};

export const stepMeta = (type: string) =>
  STEP_TYPE_META[type] ?? { label: type, color: '#64748B', desc: '' };

export function getStepLabel(type: string, t?: (k: string, opt?: any) => string): string {
  if (!t) return stepMeta(type).label;
  switch (type) {
    case 'ontology_query': return t('workbench.stepTypes.ontologyQuery', 'Consulta de ontología');
    case 'transform': return t('workbench.stepTypes.transform', 'Transformación');
    case 'llm_call': return t('workbench.stepTypes.llmCall', 'Llamada LLM');
    case 'code': return t('workbench.stepTypes.code', 'Código');
    case 'ontology_update': return t('workbench.stepTypes.ontologyUpdate', 'Actualización de ontología');
    case 'action': return t('workbench.stepTypes.action', 'Acción');
    case 'file_upload': return t('workbench.stepTypes.uploadFile', 'Subir archivo');
    case 'pipeline_node': return t('workbench.stepTypes.pipelineNode', 'Nodo de pipeline');
    default: return stepMeta(type).label;
  }
}

// ── Serializers ──────────────────────────────────────────────────────────────
// These are PURE functions (no I/O) so they can be unit-tested and reused by the
// store's export methods. They take the notebook's ordered cells and emit the
// create-request body for each target service.

/** Minimal shape we need from a notebook cell — avoids a circular import. */
export interface SerializableCell {
  id: string;
  kind: string; // 'markdown' | 'python' | 'sql' | 'step'
  source: string;
  block?: NotebookStep;
}

export interface LogicFunctionDraft {
  name: string;
  description: string;
  input_schema: never[];
  blocks: NotebookStep[];
  output_block: string | null;
}

/** Stable, deterministic block id from a cell id (logic block ids are short). */
function blockId(cell: SerializableCell, idx: number): string {
  const base = (cell.id || `cell${idx}`).replace(/[^a-zA-Z0-9_]/g, '').slice(0, 12);
  return base || `b${idx + 1}`;
}

/**
 * Notebook → Logic Function (the 1:1 mapping).
 *   - step cells       → their block verbatim (id normalized)
 *   - python/sql cells → a `code` block running the source in the sandbox
 *   - markdown cells   → dropped (narration, not executable)
 * output_block = the last executable block.
 */
export function notebookToLogicFunction(
  cells: SerializableCell[],
  name: string,
  description = '',
): LogicFunctionDraft {
  const blocks: NotebookStep[] = [];
  cells.forEach((cell, idx) => {
    if (cell.kind === 'step' && cell.block) {
      blocks.push({ ...cell.block, id: cell.block.id || blockId(cell, idx) });
    } else if (cell.kind === 'python' || cell.kind === 'sql') {
      if (!cell.source.trim()) return;
      blocks.push({
        id: blockId(cell, idx),
        type: 'code',
        label: `Code (cell ${idx + 1})`,
        config: { source: cell.source, scopes: [] },
      });
    }
    // markdown: skipped
  });
  return {
    name,
    description,
    input_schema: [],
    blocks,
    output_block: blocks.length ? blocks[blocks.length - 1].id : null,
  };
}

/**
 * Notebook → Logic Function for a single container block's own steps (the
 * logic-function-block case) — a container's `steps` are already 1:1 Logic
 * blocks (no python/sql-cell wrapping to do, unlike the whole-notebook path),
 * so this is a direct id-normalizing map.
 */
export function logicFunctionContainerToDraft(
  steps: NotebookStep[],
  name: string,
  description = '',
): LogicFunctionDraft {
  const blocks = steps.map((step, idx) => ({ ...step, id: step.id || `s${idx + 1}` }));
  return {
    name,
    description,
    input_schema: [],
    blocks,
    output_block: blocks.length ? blocks[blocks.length - 1].id : null,
  };
}

export interface PipelineNodeDraft {
  id: string;
  type: string;
  label: string;
  config: Record<string, unknown>;
  position: { x: number; y: number };
  object_type_id?: string;
  connector_id?: string;
}

export interface PipelineDraft {
  name: string;
  description: string;
  nodes: PipelineNodeDraft[];
  edges: { id: string; source: string; target: string }[];
  connector_ids: string[];
  target_object_type_id: string | null;
}

export interface PipelineMapResult {
  pipeline: PipelineDraft;
  warnings: string[];
  mappedCount: number;
  skippedCount: number;
}

/**
 * Notebook → Pipeline (best-effort, lossy). Pipelines are row-oriented ETL DAGs
 * with a fixed NodeType vocabulary, so only the steps with a clean structural
 * equivalent are mapped; the rest are skipped and reported as warnings.
 *   ontology_query  → OBJECT_SOURCE
 *   transform       → FILTER (filter_list) | MAP (otherwise)
 *   ontology_update → SINK_OBJECT
 *   pipeline_node   → passthrough to any of the 19 backend NodeTypes (validated
 *                     against NODE_TYPE_DEFS; unknown/missing node_type skipped)
 *   everything else → skipped (code, llm_call, action have no ETL node)
 */
export function notebookToPipeline(
  cells: SerializableCell[],
  name: string,
  description = '',
): PipelineMapResult {
  const steps = cells
    .map((c, idx) => ({ c, idx }))
    .filter(({ c }) => c.kind === 'step' && c.block)
    .map(({ c, idx }) => ({ step: c.block as NotebookStep, fallbackId: blockId(c, idx) }));
  return mapStepsToPipeline(steps, name, description);
}

/**
 * Notebook → Pipeline for a single container block's own steps (the
 * pipeline-block case) — no whole-notebook scan, no cross-container id
 * collisions: every step already belongs to exactly this container.
 */
export function pipelineContainerToDraft(
  steps: NotebookStep[],
  name: string,
  description = '',
): PipelineMapResult {
  return mapStepsToPipeline(
    steps.map((step, idx) => ({ step, fallbackId: step.id || `s${idx + 1}` })),
    name,
    description,
  );
}

function mapStepsToPipeline(
  steps: { step: NotebookStep; fallbackId: string }[],
  name: string,
  description: string,
): PipelineMapResult {
  const warnings: string[] = [];
  const nodes: PipelineNodeDraft[] = [];
  const edges: { id: string; source: string; target: string }[] = [];
  let target_object_type_id: string | null = null;
  let skipped = 0;

  steps.forEach(({ step: b, fallbackId }) => {
    const cfg = (b.config || {}) as Record<string, unknown>;
    const nid = b.id || fallbackId;
    const pos = { x: 80 + nodes.length * 220, y: 120 };

    if (b.type === 'ontology_query') {
      const ot = (cfg.object_type as string) || '';
      nodes.push({
        id: nid, type: 'OBJECT_SOURCE', label: b.label || 'Ontology Source',
        object_type_id: ot, config: { objectTypeId: ot, filters: cfg.filters ?? [] }, position: pos,
      });
    } else if (b.type === 'transform') {
      const isFilter = b.operation === 'filter_list';
      nodes.push({
        id: nid, type: isFilter ? 'FILTER' : 'MAP', label: b.label || (isFilter ? 'Filter' : 'Map'),
        config: { ...cfg, operation: b.operation, mappings: b.mappings }, position: pos,
      });
    } else if (b.type === 'ontology_update') {
      const ot = (cfg.object_type_id as string) || (b.object_type_id as string) || '';
      target_object_type_id = ot || target_object_type_id;
      nodes.push({
        id: nid, type: 'SINK_OBJECT', label: b.label || 'Write to ontology',
        object_type_id: ot,
        config: { objectTypeId: ot, writeMode: 'upsert', mergeKey: (cfg.match_field as string) || 'id' },
        position: pos,
      });
    } else if (b.type === 'pipeline_node') {
      const nodeType = String(b.node_type || '').toUpperCase();
      if (!VALID_PIPELINE_NODE_TYPES.has(nodeType)) {
        skipped += 1;
        warnings.push(
          `Step "${b.label || nid}" has an unknown or missing node_type ("${b.node_type ?? ''}") — skipped.`,
        );
        return;
      }
      const connectorId = (cfg.connectorId as string) || (cfg.lookupConnectorId as string) || undefined;
      const objectTypeId = (cfg.objectTypeId as string) || undefined;
      if ((nodeType === 'SINK_OBJECT' || nodeType === 'SINK_EVENT') && objectTypeId) {
        target_object_type_id = objectTypeId || target_object_type_id;
      }
      nodes.push({
        id: nid, type: nodeType, label: b.label || nodeType,
        // A CODE node from the notebook is an assistant's proposal: its code
        // never travels as runnable `code` (PIPE-10, codeConfig.asCodeProposal).
        config: nodeType === 'CODE' ? asCodeProposal(cfg, 'workbench_agent') : cfg, position: pos,
        ...(objectTypeId ? { object_type_id: objectTypeId } : {}),
        ...(connectorId ? { connector_id: connectorId } : {}),
      });
    } else {
      skipped += 1;
      warnings.push(`Step "${b.label || b.type}" (${b.type}) has no pipeline equivalent — skipped.`);
      return;
    }

    if (nodes.length > 1) {
      const prev = nodes[nodes.length - 2];
      edges.push({ id: `e${edges.length + 1}`, source: prev.id, target: nid });
    }
  });

  if (!nodes.length) {
    warnings.push('No steps could be mapped to pipeline nodes. Add ontology_query / transform / ontology_update steps.');
  }

  return {
    pipeline: {
      name, description, nodes, edges,
      connector_ids: [],
      target_object_type_id,
    },
    warnings,
    mappedCount: nodes.length,
    skippedCount: skipped,
  };
}

export interface DashboardDraft {
  name: string;
  description: string;
  kind: 'dashboard';
  object_type_ids: string[];
  components: Record<string, unknown>[];
}

/**
 * Notebook → Dashboard. A dashboard isn't a step sequence — it's widgets over an
 * object type. We derive the primary object type from the first query/update step
 * and emit a safe default widget set (count + table) that never references an
 * invented field. Returns null if no object type is referenced.
 */
export function notebookToDashboard(
  cells: SerializableCell[],
  name: string,
): DashboardDraft | null {
  let otId = '';
  for (const c of cells) {
    if (c.kind !== 'step' || !c.block) continue;
    const cfg = (c.block.config || {}) as Record<string, unknown>;
    if (c.block.type === 'ontology_query' && cfg.object_type) { otId = String(cfg.object_type); break; }
    if (c.block.type === 'ontology_update' && (cfg.object_type_id || c.block.object_type_id)) {
      otId = String(cfg.object_type_id || c.block.object_type_id); break;
    }
  }
  if (!otId) return null;

  return {
    name,
    description: `Generated from notebook "${name}"`,
    kind: 'dashboard',
    object_type_ids: [otId],
    components: [
      { id: 'w1', type: 'metric-card', title: 'Total records', objectTypeId: otId, field: 'id', aggregation: 'count', colSpan: 4 },
      { id: 'w2', type: 'data-table', title: 'Records', objectTypeId: otId, colSpan: 12 },
    ],
  };
}
